import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, type RefObject } from "react";
import { terminals } from "./store.js";

/*
 * «Свернуть во вкладку» of a session window of any kind (docs/terminal.md, "Session
 * window"): the window shrinks into its tab in the top bar, the tab pulses
 * once, and the person is back on the page they came from; a click on that
 * tab grows the window back out of it. The connections are never touched.
 *
 * The motion is a stand-in box ("ghost") fixed over the whole page and
 * animated with transform and opacity only: the window itself sits in
 * `main`, whose overflow would clip it on its way up to the top bar. Each
 * animation reads the two boxes once before it writes anything. With
 * `prefers-reduced-motion` nothing moves: the window just goes and comes back.
 */

const WORKSPACE = "/terminals";
/** The console's own address only opens its tab in the workspace: never a page to go back to. */
const CONSOLE_ROUTE = /^\/projects\/[^/]+\/servers\/[^/]+\/console\/?$/;

/** «Свернуть во вкладку»: the window into its tab. */
export const MINIMISE_MS = 180;
/** A click on the tab: the window out of it. */
export const GROW_MS = 160;
/** The tab's highlight once the window is in it. */
const PULSE_MS = 450;
/** The workspace grows from a clicked tab only if it opens this soon after the click. */
export const GROW_WITHIN_MS = 1500;

/** The last page of the panel outside the session workspace: «Свернуть во вкладку» goes back to it. */
let pageBefore: string | null = null;
export const pageBeforeSessions = (): string => pageBefore ?? "/";

/** Remembers the page to go back to on «Свернуть во вкладку» (the shell calls it on every page). */
export function useRememberPageBefore(): void {
  const href = useRouterState({ select: (s) => s.location.href });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    if (pathname !== WORKSPACE && !CONSOLE_ROUTE.test(pathname)) pageBefore = href;
  }, [href, pathname]);
}

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

const round = (value: number, digits: number) => Number(value.toFixed(digits));

/** The transform (origin at the top left) that lays a box drawn at `from` over `to`. */
export function boxTransform(from: Box, to: Box): string {
  const x = round(to.left - from.left, 2);
  const y = round(to.top - from.top, 2);
  const sx = round(to.width / from.width, 4);
  const sy = round(to.height / from.height, 4);
  return `translate(${x}px, ${y}px) scale(${sx}, ${sy})`;
}

const onScreen = (box: Box) => box.width > 0 && box.height > 0;

/** True when the person asked for less motion, or the browser cannot tell (then: no motion). */
export function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return true;
  }
}

/**
 * The session's tab in the top bar's strip, when the strip is on screen;
 * null on phones, where the «Сеансы (N)» sheet is the way back and «Свернуть во вкладку»
 * only closes the workspace.
 */
function stripTab(id: string): HTMLElement | null {
  const tab = document.querySelector<HTMLElement>(
    `[data-session-strip] [data-tab="${CSS.escape(id)}"]`,
  );
  return tab && onScreen(tab.getBoundingClientRect()) ? tab : null;
}

/** The stand-in for the window: its frame (toolbar strip on the screen colour), over everything. */
function ghost(box: Box): HTMLElement {
  const element = document.createElement("div");
  element.setAttribute("aria-hidden", "true");
  element.dataset.sessionGhost = "";
  element.className =
    "pointer-events-none fixed z-50 overflow-hidden rounded-lg border border-line bg-screen-bg shadow-xl";
  Object.assign(element.style, {
    left: `${box.left}px`,
    top: `${box.top}px`,
    width: `${box.width}px`,
    height: `${box.height}px`,
    transformOrigin: "0 0",
    willChange: "transform, opacity",
  });
  const toolbar = document.createElement("div");
  toolbar.className = "h-11 border-b border-line bg-surface";
  element.append(toolbar);
  document.body.append(element);
  return element;
}

/** A brief highlight of the tab the window went into; without the swell under reduced motion. */
function pulse(id: string, swell: boolean): void {
  const tab = stripTab(id);
  if (!tab || typeof tab.animate !== "function") return;
  const root = getComputedStyle(document.documentElement);
  const accent = root.getPropertyValue("--accent").trim() || "currentColor";
  const soft = root.getPropertyValue("--accent-soft").trim() || "transparent";
  // The ring is inset: the strip scrolls sideways and would clip one drawn outside.
  tab.animate(
    [
      { offset: 0 },
      {
        offset: 0.3,
        transform: swell ? "scale(1.06)" : "none",
        backgroundColor: soft,
        boxShadow: `inset 0 0 0 2px ${accent}`,
      },
      { offset: 1 },
    ],
    { duration: PULSE_MS, easing: "ease-out" },
  );
}

/** Shrinks the window into its session's tab, then pulses the tab. */
function minimiseIntoTab(frame: HTMLElement | null, tabId: string | null): void {
  if (!tabId) return;
  if (reducedMotion()) {
    requestAnimationFrame(() => pulse(tabId, false));
    return;
  }
  if (!frame || document.fullscreenElement || typeof frame.animate !== "function") return;
  const tab = stripTab(tabId);
  if (!tab) return;
  tab.scrollIntoView({ block: "nearest", inline: "nearest" });
  const from = frame.getBoundingClientRect();
  const to = tab.getBoundingClientRect();
  if (!onScreen(from)) return;
  const stand = ghost(from);
  const done = () => {
    stand.remove();
    pulse(tabId, true);
  };
  void stand
    .animate(
      [
        { transform: "none", opacity: 1 },
        { transform: boxTransform(from, to), opacity: 0.35 },
      ],
      { duration: MINIMISE_MS, easing: "cubic-bezier(0.4, 0, 1, 1)", fill: "forwards" },
    )
    .finished.then(done, done);
}

let growFrom: { box: Box; at: number } | null = null;

/** The box a workspace opening by `at + GROW_WITHIN_MS` grows from. */
export function rememberGrowFrom(box: Box, at: number): void {
  growFrom = { box, at };
}

/** The box to grow from, once: null when there is none or it is too old. */
export function takeGrowFrom(now: number): Box | null {
  const pending = growFrom;
  growFrom = null;
  return pending && now - pending.at <= GROW_WITHIN_MS ? pending.box : null;
}

/**
 * A click on a session's tab while the workspace is hidden: the workspace
 * will grow out of that tab. Nothing while it is on screen (switching
 * tabs), on phones, or under reduced motion.
 */
export function growFromTabNext(tabId: string): void {
  if (terminals.snapshot().visible || reducedMotion()) return;
  const tab = stripTab(tabId);
  if (tab) rememberGrowFrom(tab.getBoundingClientRect(), performance.now());
}

/** Grows the window out of `from`: the stand-in grows, the window fades in under it. */
function growWindow(frame: HTMLElement, from: Box): void {
  if (typeof frame.animate !== "function") return;
  const to = frame.getBoundingClientRect();
  if (!onScreen(to)) return;
  const stand = ghost(to);
  const done = () => stand.remove();
  void stand
    .animate(
      [
        { transform: boxTransform(to, from), opacity: 0.35 },
        { transform: "none", opacity: 1, offset: 0.8 },
        { transform: "none", opacity: 0 },
      ],
      { duration: GROW_MS, easing: "cubic-bezier(0, 0, 0.2, 1)", fill: "forwards" },
    )
    .finished.then(done, done);
  frame.animate([{ opacity: 0 }, { opacity: 0, offset: 0.7 }, { opacity: 1 }], {
    duration: GROW_MS,
  });
}

/**
 * The window's minimise and restore: grows out of the clicked tab when it
 * opens, and gives «Свернуть во вкладку» — into the tab, back to the page before.
 */
export function useSessionMinimise(frame: RefObject<HTMLElement | null>): () => void {
  const navigate = useNavigate();
  useLayoutEffect(() => {
    const from = takeGrowFrom(performance.now());
    if (from && frame.current) growWindow(frame.current, from);
  }, [frame]);
  return () => {
    minimiseIntoTab(frame.current, terminals.snapshot().active);
    void navigate({ href: pageBeforeSessions() });
  };
}
